import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FLOWS_QUERY_KEY,
  SMS_TEMPLATES_QUERY_KEY,
} from 'constants/QueryKeys.constants';
import { PATHS } from 'routes/routes.constants';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';
import { useRouter } from '@polyai/common/hooks/useRouter';

import api from 'api';
import type { MinimalFlow } from 'api/resources/flows/types';
import { curryPostFunction } from 'api/utils';
import { useDraft } from 'hooks/useDraft';
import useDraftMutation from 'hooks/useDraftMutation';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import { useRestApiEnabled } from 'hooks/useStreamSwitch';
import { useToast } from 'hooks/useToast';
import { generateNextPath } from 'lib/routeHelper';

const DEFAULT_FLOW_NAME = 'flow';

export const getUniqueName = (
  names: string[],
  defaultName: string = DEFAULT_FLOW_NAME,
  separator: string = ' ',
) => {
  let i = 1;
  let name = defaultName;
  while (names?.find(fname => fname === name)) {
    name = `${defaultName}${separator}${i}`;
    i++;
  }
  return name;
};

export const isNameUnique = (flows: MinimalFlow[], name: string) =>
  !flows?.find(f => f.name === name);

const useFlows = (
  enabled: boolean = true,
  overrideData?: MinimalFlow[] | null, // pass null to override with empty data
) => {
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();

  const isRestApiEnabled = useRestApiEnabled();

  const queryKey = [accountId, projectId, FLOWS_QUERY_KEY];

  const toast = useToast();
  const queryClient = useQueryClient();

  const { isFeatureOn } = useIsFeatureOn();
  const { isProjectResourceEnabled } = useProjectResource();
  const { onProjectUpdate } = useDraft();
  const { pushUrl } = useRouter();

  const goToFlow = useCallback(
    (flowId: string) =>
      pushUrl(
        generateNextPath(PATHS.FLOW_DETAILS, { accountId, projectId, flowId }),
      ),
    [accountId, projectId, pushUrl],
  );

  const isEnabled =
    enabled &&
    isFeatureOn('jobs_to_be_done') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.FLOWS);

  const {
    data: flows,
    isLoading: isLoadingFlows,
    error: flowsError,
  } = useQuery(
    queryKey,
    overrideData === undefined
      ? () => api.getFlows(accountId, projectId)
      : () => overrideData || undefined,
    {
      enabled: !!accountId && !!projectId && isEnabled && isRestApiEnabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: createFlow, isLoading: isCreatingFlow } = useDraftMutation(
    curryPostFunction(api.createFlow)({ accountId, projectId }),
    {
      mutationKey: queryKey,
      retry: false,
      onSuccess: () => {
        queryClient
          .invalidateQueries(queryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        toast.success({
          title: 'Flow added',
        });
        onProjectUpdate();
      },
      onError: () => {
        toast.error({
          title: 'Failed to add flow',
        });
      },
    },
  );

  const { mutate: duplicateFlow, isLoading: isDuplicatingFlow } =
    useDraftMutation(
      curryPostFunction(api.duplicateFlow)({ accountId, projectId }),
      {
        mutationKey: queryKey,
        retry: false,
        onSuccess: data => {
          queryClient
            .invalidateQueries(queryKey)
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          toast.success({
            title: 'Flow duplicated',
          });
          onProjectUpdate();
          goToFlow(data.id);
        },
        onError: () => {
          toast.error({
            title: 'Failed to duplicate flow',
          });
        },
      },
    );

  const { mutate: deleteFlow, isLoading: isDeletingFlow } = useDraftMutation(
    curryPostFunction(api.deleteFlow)({ accountId, projectId }),
    {
      mutationKey: queryKey,
      meta: {
        invalidates: [[accountId, projectId, ...SMS_TEMPLATES_QUERY_KEY]],
      },
      retry: false,
      onSuccess: () => {
        queryClient
          .invalidateQueries(queryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        toast.success({
          title: 'Flow deleted',
        });
        onProjectUpdate();
      },
      onError: () => {
        toast.error({
          title: 'Failed to delete flow',
        });
      },
    },
  );

  return {
    accountId,
    projectId,
    flows,
    isLoadingFlows: isEnabled && isLoadingFlows,
    flowsError,
    createFlow,
    isCreatingFlow,
    duplicateFlow,
    isDuplicatingFlow,
    deleteFlow,
    isDeletingFlow,
    goToFlow,
  };
};

export default useFlows;
